import type { SortDescriptor } from "@tql/ui-components-react/kendo/data-query";
import type {
	GridCustomCellProps,
	GridCustomFooterCellProps,
	GridPagerSettings,
	GridSelectionChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { lineChartUp_03 } from "@tql/ui-components-react/untitled/icons";

import type {
	SalesPerformance,
	SalesPerformanceRow,
} from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";
import { formatNumber } from "~/shared/utils/string-fns.ts";

import { ArDashboardCard } from "../ar-dashboard-card/ar-dashboard-card.tsx";

type SalesPerformanceCardProps = {
	data: SalesPerformance;
	isQuoteSummaryUnavailable?: boolean;
	isSalesPerformanceUnavailable?: boolean;
	isLoading?: boolean;
	isFetching?: boolean;
	selectedCustomerId?: number | null;
	onCustomerSelect?: (customerId: number | null) => void;
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
});

const ratioFormatter = new Intl.NumberFormat("en-US", {
	style: "percent",
	minimumFractionDigits: 0,
	maximumFractionDigits: 1,
});

const DEFAULT_SORT: SortDescriptor[] = [
	{ field: "brokerageRevenue", dir: "desc" },
];

const PAGEABLE: GridPagerSettings = {
	buttonCount: 4,
	pageSizes: [25, 50, 75, 100],
	info: true,
};

type SalesPerformanceGridRow = SalesPerformanceRow & {
	id: number;
	shipmentsDisplay: string;
	brokerageRevenueDisplay: string;
	quotesDisplay: string;
	quoteRatioDisplay: string;
	selected?: boolean;
};

const toGridRow = (
	row: SalesPerformanceRow,
	selectedCustomerId: number | null | undefined,
): SalesPerformanceGridRow => ({
	...row,
	customerName: row.customerName.trim(),
	id: row.customerId,
	shipmentsDisplay: formatNumber(row.shipments),
	brokerageRevenueDisplay: currencyFormatter.format(row.brokerageRevenue),
	quotesDisplay: row.quotes === null ? "-" : formatNumber(row.quotes),
	quoteRatioDisplay:
		row.quoteRatio === null ? "-" : ratioFormatter.format(row.quoteRatio),
	selected: selectedCustomerId === row.customerId,
});

// Hide a customer only when every metric is zero or null. Customers with
// quote activity but no shipments (or vice versa) must still display.
const hasActivity = (row: SalesPerformanceRow): boolean =>
	row.shipments !== 0 ||
	(row.quotes ?? 0) !== 0 ||
	(row.quoteRatio ?? 0) !== 0 ||
	row.brokerageRevenue !== 0;

const SalesPerformanceCardContent = ({
	data,
	isQuoteSummaryUnavailable = false,
	isSalesPerformanceUnavailable = false,
	isLoading = false,
	isFetching = false,
	selectedCustomerId,
	onCustomerSelect,
}: SalesPerformanceCardProps) => {
	// Centralized grid state persists the items-per-page selection for this grid
	// (sorting stays uncontrolled via `defaultSort`).
	const { skip, take, handlePageChange, isLoadingPreference } = useGridState(
		GridIdentifiers["AR Dashboard Sales Performance"],
	);

	const showSkeleton = isLoading || isFetching;
	const visibleRows = data.rows.filter((row) => hasActivity(row));
	const rows = visibleRows.map((row) => toGridRow(row, selectedCustomerId));

	const totalShipments = visibleRows.reduce(
		(sum, row) => sum + row.shipments,
		0,
	);
	const totalQuotes = visibleRows.reduce(
		(sum, row) => sum + (row.quotes ?? 0),
		0,
	);

	const handleSelectionChange = (event: GridSelectionChangeEvent) => {
		// For row selection (no checkbox), Kendo exposes the clicked row via
		// `startDataItem`/`endDataItem`. `dataItem` is null and indexing
		// `dataItems` by `startRowIndex` breaks once the grid is sorted.
		const selectedItem = (event.startDataItem ?? event.endDataItem) as
			| SalesPerformanceGridRow
			| undefined;
		if (!selectedItem) {
			onCustomerSelect?.(null);
			return;
		}
		const nextId =
			selectedCustomerId === selectedItem.customerId
				? null
				: selectedItem.customerId;
		onCustomerSelect?.(nextId);
	};

	const columns = [
		{
			field: "customerName",
			title: "Customer Name",
			filterable: false,
			cells: {
				footerCell: (props: GridCustomFooterCellProps) => (
					<GridTd tdProps={props.tdProps}>
						<strong>TOTAL</strong>
					</GridTd>
				),
			},
			editable: false,
		},
		{
			field: "shipments",
			title: "Shipments",
			editable: false,
			cells: {
				data: (props: GridCustomCellProps) => (
					<GridTd tdProps={props.tdProps}>
						{(props.dataItem as SalesPerformanceGridRow).shipmentsDisplay}
					</GridTd>
				),
				footerCell: (props: GridCustomFooterCellProps) => (
					<GridTd tdProps={props.tdProps}>
						<strong>
							{isSalesPerformanceUnavailable
								? "-"
								: formatNumber(totalShipments)}
						</strong>
					</GridTd>
				),
			},
		},
		{
			field: "quotes",
			title: "Quotes",
			editable: false,
			cells: {
				data: (props: GridCustomCellProps) => (
					<GridTd tdProps={props.tdProps}>
						{(props.dataItem as SalesPerformanceGridRow).quotesDisplay}
					</GridTd>
				),
				footerCell: (props: GridCustomFooterCellProps) => (
					<GridTd tdProps={props.tdProps}>
						<strong>
							{isQuoteSummaryUnavailable ? "-" : formatNumber(totalQuotes)}
						</strong>
					</GridTd>
				),
			},
		},
		{
			field: "quoteRatio",
			title: "SQ Ratio",
			editable: false,
			cells: {
				data: (props: GridCustomCellProps) => (
					<GridTd tdProps={props.tdProps}>
						{(props.dataItem as SalesPerformanceGridRow).quoteRatioDisplay}
					</GridTd>
				),
				footerCell: (props: GridCustomFooterCellProps) => (
					<GridTd tdProps={props.tdProps}>
						<strong>-</strong>
					</GridTd>
				),
			},
		},
		{
			field: "brokerageRevenue",
			title: "Brokerage Revenue",
			cells: {
				data: (props: GridCustomCellProps) => (
					<GridTd tdProps={props.tdProps}>
						{
							(props.dataItem as SalesPerformanceGridRow)
								.brokerageRevenueDisplay
						}
					</GridTd>
				),
				footerCell: (props: GridCustomFooterCellProps) => (
					<GridTd tdProps={props.tdProps}>
						<strong>
							{isSalesPerformanceUnavailable
								? "-"
								: currencyFormatter.format(data.totalBrokerageRevenue)}
						</strong>
					</GridTd>
				),
			},
			editable: false,
		},
	];

	return (
		<div>
			<TraxGrid
				columns={columns}
				data={rows}
				dataItemKey={"id"}
				defaultSort={DEFAULT_SORT}
				exactLoadingRows={5}
				loading={showSkeleton || isLoadingPreference}
				onPageChange={handlePageChange}
				onSelectionChange={handleSelectionChange}
				pageable={PAGEABLE}
				selectable={
					onCustomerSelect ? { enabled: true, mode: "single" } : undefined
				}
				skip={skip}
				sortable={true}
				take={take}
			/>
		</div>
	);
};

export const SalesPerformanceCard = (props: SalesPerformanceCardProps) => {
	return (
		<ArDashboardCard
			content={<SalesPerformanceCardContent {...props} />}
			icon={lineChartUp_03}
			title={"Sales Performance"}
			variant={"alt"}
		/>
	);
};
